import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, Info } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';

interface AlertBadgeProps {
  id: string; // Unique ID for the alert (e.g., 'sade-sati-2024', 'markesh-mars')
  label: string;
  type?: 'warning' | 'danger';
  advice?: string;
  className?: string;
  onRead?: () => void;
}

export const AlertBadge: React.FC<AlertBadgeProps> = ({ 
  id, 
  label, 
  type = 'warning', 
  advice = "These cycles represent periods of discipline and transformation. They are not 'bad', but suggest a time for proactive remedies and inner harmony.",
  className = '',
  onRead
}) => {
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [showAdvice, setShowAdvice] = useState(false);
  const badgeContainerRef = useRef<HTMLDivElement>(null);

  // Global Outside-Click & Escape Dismissal (V5.15-R7)
  useClickOutside(badgeContainerRef, showAdvice, () => setShowAdvice(false));

  useEffect(() => {
    const acknowledged = localStorage.getItem(`alert_ack_${id}`);
    if (acknowledged) {
      setIsAcknowledged(true);
    }
  }, [id]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isAcknowledged) {
      localStorage.setItem(`alert_ack_${id}`, 'true');
      setIsAcknowledged(true);
      if (onRead) onRead();
    }
    setShowAdvice(!showAdvice);
  };

  const colors = type === 'danger' 
    ? 'bg-rose-100 text-rose-800 border-rose-300' 
    : 'bg-astro-secondary/15 text-astro-secondary border-[#E2DED5]';

  return (
    <div 
      className={`relative inline-block ${className}`} 
      ref={badgeContainerRef}
      onMouseEnter={() => setShowAdvice(true)}
      onMouseLeave={() => setShowAdvice(false)}
    >
      <button 
        onClick={handleToggle}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all shadow-sm cursor-pointer ${colors} ${!isAcknowledged ? 'animate-urgent-blink' : ''}`}
      >
        <AlertCircle className="w-3 h-3" />
        <span>{label}</span>
      </button>

      {showAdvice && (
        <div className="absolute z-50 top-full mt-2 left-0 w-64 bg-white border border-astro-border rounded-xl shadow-xl p-3 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-start gap-2 mb-2">
            <Info className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
            <div className="text-[11px] font-bold text-astro-primary uppercase tracking-wider">Astro Advice</div>
          </div>
          <p className="text-[11px] text-astro-text leading-relaxed italic">
            {advice}
          </p>
          <div className="mt-2 pt-2 border-t border-astro-border/50 flex justify-end">
            <button 
              onClick={() => setShowAdvice(false)}
              className="text-[10px] font-bold text-astro-primary hover:underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
